import React, { useEffect, useMemo } from "react";
import type { TFunction } from "i18next";
import { U2000IdColumnDef, type U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import { NS, U3121ROLES } from "../../../../U3121";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import useU2000AxiosAuthenticated from "../../../../../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useU3121Context } from "../../../../U3121Context";
import type { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { doDeleteSubProjectData, luHandleAppiError, type U3121Project, type U3121SubProject } from "../../../../U3121Service";
import { useU2000DeleteHandlers } from "../../../../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000HandleApiError } from "../../../../../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../../../../../u2000Core/context/toast/U2000ToastContext";
import U3121SubProjectDeleteDlg from "../../../common/U3121SubProjectDeleteDlg";
import { u3121ProjectSubProjectsHandler } from "./U3121ProjectSubProjectsHandlers";

export interface U3121ProjectFormSubProjectsHandlers {
  getData: () => U3121Project | null;
  setData: (data: U3121Project) => void;
  getFormState: () => string;
}

interface U3121ProjectSubProjectsBodyProps {
  formStateHandlers: U3121ProjectFormSubProjectsHandlers;
}

function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    U2000IdColumnDef,
    {
      name: "subProjetNumber",
      label: t("lblSubProjetNumber", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[15%]",
      type: "string",
      align: "left",
    },
    {
      name: "subProjetName",
      label: t("lblSubProjetName", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "subProjectStatus",
      label: t("lblSubProjectStatus", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "boolean",
      align: "left",
      display: (_column: U2000TableColumn, value: unknown, _row: unknown, _allRowSelected: boolean, _rowUpdated?: (row: unknown) => void) => {
        return value ? (
          <span className="rounded-sm bg-green-100 px-2 py-0.5 text-xs text-green-800">{t("valActive", { ns: NS })}</span>
        ) : (
          <span className="rounded-sm bg-gray-100 px-2 py-0.5 text-xs text-gray-800">{t("valInactive", { ns: NS })}</span>
        );
      },
    },
  ];
}

export const U3121ProjectSubProjectsBody: React.FC<U3121ProjectSubProjectsBodyProps> = ({ formStateHandlers }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const { hasRole } = useU2000AuthContext();
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const { luStateHandlers, gridProjectSubProjectStateHandlers } = useU3121Context();

  const u3121ProjectSubProjectsHandlers = u3121ProjectSubProjectsHandler({
    luStateHandlers: luStateHandlers as ReturnType<typeof import("../../../../../u2000Core/reducer/formReducer/U2000LUHandlers").useU2000LUStateHandlers>,
    gridStateHandlers: gridProjectSubProjectStateHandlers as ReturnType<typeof useU2000GridStateHandlers<U3121SubProject>>,
    navigate,
  });

  const columns = useMemo(() => getColumnsDef(t), [t]);

  useEffect(() => {
    try {
      const currentColumns = gridProjectSubProjectStateHandlers.getColumns?.() ?? [];
      if (JSON.stringify(currentColumns) !== JSON.stringify(columns)) {
        gridProjectSubProjectStateHandlers.setSize(9999);
        gridProjectSubProjectStateHandlers.setColumns(columns);
        gridProjectSubProjectStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
        gridProjectSubProjectStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));
      }

      const project = formStateHandlers.getData?.();
      const subprojects = (project?.subprojects as U3121SubProject[]) ?? [];
      const currentData = gridProjectSubProjectStateHandlers.getData?.() ?? [];
      if (JSON.stringify(currentData) !== JSON.stringify(subprojects)) {
        gridProjectSubProjectStateHandlers.setData(Array.isArray(subprojects) ? subprojects : []);
        gridProjectSubProjectStateHandlers.setTotalRows(Array.isArray(subprojects) ? subprojects.length : 0);
      }
    } catch (e) {
      const cols = columns;
      gridProjectSubProjectStateHandlers.setColumns(cols);
      const project = formStateHandlers.getData?.();
      const subprojects = (project?.subprojects as U3121SubProject[]) ?? [];
      gridProjectSubProjectStateHandlers.setData(Array.isArray(subprojects) ? subprojects : []);
      gridProjectSubProjectStateHandlers.setTotalRows(Array.isArray(subprojects) ? subprojects.length : 0);
    }
  }, [formStateHandlers.getData?.(), t]);

  useEffect(() => {
    if (u3121ProjectSubProjectsHandlers.dataChangedEvent === 0) return;
    const project = formStateHandlers.getData?.();
    if (!project) return;
    const subprojects = gridProjectSubProjectStateHandlers.getData();
    formStateHandlers.setData({
      ...project,
      subprojects: subprojects ?? [],
    });
  }, [u3121ProjectSubProjectsHandlers.dataChangedEvent]);

  function disabledUpdateBtn(): boolean {
    const selectedRows = gridProjectSubProjectStateHandlers.getSelectedRows();
    return formStateHandlers.getFormState?.() === U2000FormStateEnumState.EDIT || selectedRows.length !== 1;
  }

  function disableDeleteBtn(): boolean {
    const selectedRows = gridProjectSubProjectStateHandlers.getSelectedRows();
    return formStateHandlers.getFormState?.() === U2000FormStateEnumState.EDIT || selectedRows.length === 0;
  }

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => gridProjectSubProjectStateHandlers.getSelectedRows(),
    doDeleteFn: async (_id, row) => {
      if (!projectId) throw new Error("projectId missing");
      return doDeleteSubProjectData(u2000AxiosAuthenticated, projectId, (row as U3121SubProject).id, row as U3121SubProject);
    },
    onSuccess: (deletedRows) => {
      let newData = gridProjectSubProjectStateHandlers.getData();
      let totalRows = gridProjectSubProjectStateHandlers.getTotalRows();

      deletedRows.forEach((row) => {
        toast.success(t("success0003", { key: (row as U3121SubProject).subProjetNumber, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== (row as U3121SubProject)?.id);
        totalRows -= 1;
      });

      gridProjectSubProjectStateHandlers.setData(newData);
      gridProjectSubProjectStateHandlers.setTotalRows(totalRows);

      const project = formStateHandlers.getData?.();
      if (project) {
        formStateHandlers.setData({ ...project, subprojects: newData });
      }
      u3121ProjectSubProjectsHandlers.setDataChangedEvent(u3121ProjectSubProjectsHandlers.dataChangedEvent + 1);
    },
    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAddSubProject", { ns: NS }),
        handleClick: u3121ProjectSubProjectsHandlers.onAdd,
        hasRole: hasRole(U3121ROLES.ADD),
        disabled: formStateHandlers.getFormState?.() === U2000FormStateEnumState.EDIT || !projectId || projectId === "create",
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u3121ProjectSubProjectsHandlers.onModify,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: handleDelete.handleDelete,
        hasRole: hasRole(U3121ROLES.EDIT),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u3121ProjectSubProjectsHandlers, formStateHandlers, projectId, handleDelete.handleDelete]
  );

  return (
    <>
      <U3121SubProjectDeleteDlg handleDelete={handleDelete} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridProjectSubProjectStateHandlers}
        rowClickFn={(row) => u3121ProjectSubProjectsHandlers.onRowClick(row as U3121SubProject)}
        allowRowSelection={formStateHandlers.getFormState?.() !== U2000FormStateEnumState.EDIT}
        label={t("lblSubProjects", {
          count: gridProjectSubProjectStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};